import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Layers,
  Globe2,
  ShieldCheck,
  Database,
  LineChart as LineIcon,
  GitBranch,
  FileText,
  CheckCircle2,
  Mail,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { useI18n } from "../i18n/I18nContext";
import { useSmoothScroll } from "../components/SmoothScroll";

const IMG = {
  salt: "https://static.prod-images.emergentagent.com/jobs/699dae66-7900-434a-9e1b-0ef0ab87b96d/images/f33354094181325f147b5c076e178062f9eae2b4bbd86b0cca603cf6d6117243.jpeg",
  rig: "https://static.prod-images.emergentagent.com/jobs/699dae66-7900-434a-9e1b-0ef0ab87b96d/images/4ec8821b18c649f7c86ee56cfb903d9ef08e106c7d6b804fd60b2c3e48dac7da.jpeg",
  data: "https://images.unsplash.com/photo-1748609622257-bb917eda4d14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  strata:
    "https://images.unsplash.com/photo-1756361629841-72135af95441?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const SECTOR_DATA = {
  litio: [100, 112, 121, 134, 149, 168],
  petroleo: [100, 104, 109, 111, 118, 124],
  gas: [100, 103, 101, 106, 109, 113],
  cobre: [100, 96, 108, 119, 127, 141],
};
const SECTOR_COLOR = {
  litio: "#0284C7",
  petroleo: "#C85A32",
  gas: "#D97706",
  cobre: "#475569",
};
const LABELS = ["T1", "T2", "T3", "T4", "T5", "T6"];

const WHY_ICONS = [BarChart3, Globe2, Layers, ShieldCheck];
const STEP_ICONS = [Database, LineIcon, GitBranch, FileText];

function Eyebrow({ children, dark = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] ${
        dark ? "text-terracotta" : "text-terracotta"
      }`}
    >
      <span className="h-px w-6 bg-terracotta" />
      {children}
    </span>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const { t } = useI18n();
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollTo } = useSmoothScroll();
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const titleLines = t("hero.titleLines");
  const tickers = t("tickers");
  const introStats = t("intro.stats");
  const whyItems = t("why.items");
  const howSteps = t("how.steps");
  const transPoints = t("transparency.points");
  const sectors = t("dashboard.sectors");
  const metrics = t("dashboard.metrics");

  const [sector, setSector] = useState("litio");
  const chartData = useMemo(
    () => LABELS.map((label, i) => ({ label, value: SECTOR_DATA[sector][i] })),
    [sector]
  );

  useEffect(() => {
    if (location.hash) {
      const id = location.hash;
      setTimeout(() => scrollTo(id), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contact form
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = t("contact.errName");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t("contact.errEmail");
    if (!form.message.trim()) errs.message = t("contact.errMessage");
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      navigate("/thank_you");
    }
  };

  return (
    <div className="bg-sand">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden bg-ink">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={IMG.salt}
            alt="Salares de litio en el noroeste argentino"
            className="h-[125%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/70 to-ink" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/40 to-transparent" />
        </motion.div>

        <motion.div
          style={{ opacity: heroFade }}
          className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-5 sm:px-8 pt-28 pb-20"
        >
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/80 backdrop-blur-sm"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
              {t("hero.badge")}
            </span>
          </motion.div>

          <h1 className="mt-7 max-w-4xl font-heading text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.03] tracking-tight text-white">
            {Array.isArray(titleLines) &&
              titleLines.map((line, i) => (
                <span key={i} className="reveal-line-mask">
                  <motion.span
                    className="block"
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{
                      duration: 0.95,
                      delay: 0.15 * i + 0.25,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    {i === titleLines.length - 1 ? (
                      <span className="text-terracotta">{line}</span>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-white/70"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-3.5"
          >
            <button
              type="button"
              data-testid="hero-cta-primary"
              onClick={() => scrollTo("#circuit")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-terracotta-hover hover:-translate-y-0.5"
            >
              {t("hero.ctaPrimary")}
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              type="button"
              data-testid="hero-cta-secondary"
              onClick={() => scrollTo("#contact")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 px-7 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/10"
            >
              {t("hero.ctaSecondary")}
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-ink/10 bg-ink py-4 overflow-hidden">
        <div className="marquee-paused">
          <div className="marquee-track gap-10">
            {Array.isArray(tickers) &&
              [...tickers, ...tickers].map((tk, i) => (
                <span
                  key={i}
                  className="flex shrink-0 items-center gap-3 font-mono text-xs uppercase tracking-widest text-white/60"
                >
                  <span className="text-white/40">{tk.label}</span>
                  <span className="text-white">{tk.value}</span>
                  <span className="text-terracotta">{tk.delta}</span>
                  <span className="ml-6 text-white/15">/</span>
                </span>
              ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow>{t("intro.eyebrow")}</Eyebrow>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">
              {t("intro.heading")}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-mineral">{t("intro.p1")}</p>
            <p className="mt-4 text-base leading-relaxed text-mineral">{t("intro.p2")}</p>
            <div className="mt-9 grid grid-cols-3 gap-6 border-t border-ink/10 pt-8">
              {Array.isArray(introStats) &&
                introStats.map((s, i) => (
                  <div key={i}>
                    <div className="font-heading text-3xl font-extrabold text-terracotta">
                      {s.value}
                    </div>
                    <div className="mt-1.5 text-xs leading-snug text-mineral">{s.label}</div>
                  </div>
                ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={IMG.rig}
                  alt="Yacimiento de petróleo y gas en Vaca Muerta, Neuquén"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl border border-ink/10 bg-white p-5 shadow-[0_20px_50px_rgba(10,25,47,0.12)]">
                <div className="font-mono text-[10px] uppercase tracking-widest text-mineral">
                  Vaca Muerta · Neuquén
                </div>
                <div className="mt-1 font-heading text-2xl font-extrabold text-ink">
                  92 <span className="text-sm text-mineral">MMm³/d</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY — bento */}
      <section id="analysis" className="scroll-mt-24 bg-white border-y border-ink/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
          <Reveal>
            <Eyebrow>{t("why.eyebrow")}</Eyebrow>
            <h2 className="mt-5 max-w-3xl font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">
              {t("why.title")}
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(whyItems) &&
              whyItems.map((item, i) => {
                const Icon = WHY_ICONS[i];
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div
                      data-testid={`why-card-${i}`}
                      className="group h-full rounded-2xl border border-ink/10 bg-sand p-7 transition-all duration-300 hover:border-terracotta/40 hover:-translate-y-1"
                    >
                      <span className="grid h-12 w-12 place-items-center rounded-xl bg-ink text-terracotta transition-colors duration-300 group-hover:bg-terracotta group-hover:text-white">
                        <Icon size={22} />
                      </span>
                      <h3 className="mt-6 font-heading text-lg font-bold text-ink">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-mineral">{item.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
          </div>
        </div>
      </section>

      {/* HOW — steps */}
      <section id="circuit" className="scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>{t("how.eyebrow")}</Eyebrow>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">
                {t("how.title")}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-mineral">{t("how.subtitle")}</p>
            </Reveal>
          </div>

          <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-ink/10 bg-ink/10 md:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(howSteps) &&
              howSteps.map((step, i) => {
                const Icon = STEP_ICONS[i];
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div
                      data-testid={`step-card-${i}`}
                      className="group relative h-full bg-white p-8 transition-colors duration-300 hover:bg-ink"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-sm text-terracotta">{step.n}</span>
                        <Icon
                          size={20}
                          className="text-mineral transition-colors group-hover:text-terracotta"
                        />
                      </div>
                      <h3 className="mt-8 font-heading text-lg font-bold text-ink transition-colors group-hover:text-white">
                        {step.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-mineral transition-colors group-hover:text-white/70">
                        {step.desc}
                      </p>
                    </div>
                  </Reveal>
                );
              })}
          </div>
        </div>
      </section>

      {/* DASHBOARD */}
      <section id="dashboard" className="scroll-mt-24 bg-ink text-white grain">
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-4">
              <Reveal>
                <Eyebrow dark>{t("dashboard.eyebrow")}</Eyebrow>
                <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                  {t("dashboard.title")}
                </h2>
                <p className="mt-5 text-base leading-relaxed text-white/60">
                  {t("dashboard.subtitle")}
                </p>

                <div className="mt-8 grid grid-cols-3 gap-4">
                  {Array.isArray(metrics) &&
                    metrics.map((m, i) => (
                      <div key={i} className="rounded-xl border border-white/10 bg-white/5 p-4">
                        <div className="font-heading text-2xl font-extrabold text-terracotta">
                          {m.value}
                        </div>
                        <div className="mt-1 font-mono text-[10px] uppercase tracking-wide text-white/50">
                          {m.unit}
                        </div>
                        <div className="mt-1.5 text-xs leading-snug text-white/70">{m.label}</div>
                      </div>
                    ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-8">
              <Reveal delay={0.15}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-7 backdrop-blur-sm">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <p className="font-mono text-xs uppercase tracking-widest text-white/50">
                      {t("dashboard.chartTitle")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {Array.isArray(sectors) &&
                        sectors.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            data-testid={`sector-toggle-${s.id}`}
                            onClick={() => setSector(s.id)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                              sector === s.id
                                ? "text-white"
                                : "bg-white/5 text-white/60 hover:bg-white/10"
                            }`}
                            style={
                              sector === s.id ? { backgroundColor: SECTOR_COLOR[s.id] } : undefined
                            }
                          >
                            {s.name}
                          </button>
                        ))}
                    </div>
                  </div>

                  <div className="mt-6 h-[300px] w-full" data-testid="dashboard-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                        <XAxis
                          dataKey="label"
                          stroke="rgba(255,255,255,0.4)"
                          tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          stroke="rgba(255,255,255,0.4)"
                          tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }}
                          axisLine={false}
                          tickLine={false}
                          domain={[80, "auto"]}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "#0A192F",
                            border: "1px solid rgba(255,255,255,0.15)",
                            borderRadius: 12,
                            color: "#fff",
                            fontFamily: "JetBrains Mono",
                            fontSize: 12,
                          }}
                          cursor={{ stroke: "rgba(255,255,255,0.2)" }}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke={SECTOR_COLOR[sector]}
                          strokeWidth={3}
                          dot={{ r: 4, fill: SECTOR_COLOR[sector], strokeWidth: 0 }}
                          activeDot={{ r: 6 }}
                          animationDuration={700}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="mt-5 text-xs leading-relaxed text-white/40">{t("dashboard.note")}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="scroll-mt-24 bg-white border-b border-ink/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={IMG.strata}
                  alt="Estratos minerales y formaciones geológicas"
                  className="aspect-[5/4] w-full object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 to-transparent" />
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <Eyebrow>{t("transparency.eyebrow")}</Eyebrow>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">
                {t("transparency.title")}
              </h2>
              <p className="mt-6 text-base leading-relaxed text-mineral">{t("transparency.p1")}</p>
              <p className="mt-4 text-base leading-relaxed text-mineral">{t("transparency.p2")}</p>
              <ul className="mt-8 space-y-4">
                {Array.isArray(transPoints) &&
                  transPoints.map((p, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-terracotta" />
                      <span className="text-[15px] leading-relaxed text-ink">{p}</span>
                    </li>
                  ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24 lg:py-32">
          <div className="grid gap-14 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-ink">
                {t("contact.title")}
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-mineral">
                {t("contact.subtitle")}
              </p>
              <div className="mt-8 flex items-center gap-3 text-sm text-mineral">
                <Mail size={17} className="text-terracotta" />
                <span>{t("contact.orEmail")}</span>
                <a
                  data-testid="contact-email-link"
                  href="mailto:consultas@litiodata.com"
                  className="font-semibold text-ink underline underline-offset-4 hover:text-terracotta"
                >
                  consultas@litiodata.com
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <form
                data-testid="contact-form"
                onSubmit={onSubmit}
                noValidate
                className="rounded-2xl border border-ink/10 bg-white p-7 sm:p-9 shadow-[0_20px_60px_rgba(10,25,47,0.06)]"
              >
                <div className="space-y-5">
                  <div>
                    <label
                      htmlFor="cf-name"
                      className="mb-2 block font-mono text-xs uppercase tracking-widest text-mineral"
                    >
                      {t("contact.name")}
                    </label>
                    <input
                      id="cf-name"
                      data-testid="contact-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("contact.namePh")}
                      className="w-full rounded-xl border border-ink/15 bg-sand px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-mineral/60 focus:border-terracotta focus:bg-white"
                    />
                    {errors.name && (
                      <p data-testid="error-name" className="mt-2 text-xs text-red-600">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="cf-email"
                      className="mb-2 block font-mono text-xs uppercase tracking-widest text-mineral"
                    >
                      {t("contact.email")}
                    </label>
                    <input
                      id="cf-email"
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder={t("contact.emailPh")}
                      className="w-full rounded-xl border border-ink/15 bg-sand px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-mineral/60 focus:border-terracotta focus:bg-white"
                    />
                    {errors.email && (
                      <p data-testid="error-email" className="mt-2 text-xs text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="cf-message"
                      className="mb-2 block font-mono text-xs uppercase tracking-widest text-mineral"
                    >
                      {t("contact.message")}
                    </label>
                    <textarea
                      id="cf-message"
                      data-testid="contact-message"
                      rows={4}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder={t("contact.messagePh")}
                      className="w-full resize-none rounded-xl border border-ink/15 bg-sand px-4 py-3.5 text-sm text-ink outline-none transition-colors placeholder:text-mineral/60 focus:border-terracotta focus:bg-white"
                    />
                    {errors.message && (
                      <p data-testid="error-message" className="mt-2 text-xs text-red-600">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-terracotta"
                  >
                    {t("contact.submit")}
                    <ArrowUpRight
                      size={17}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
